import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { AirQualityComponent } from "@/db/definitions";
import {
  AIR_COMPONENT_BANDS,
  AIR_QUALITY_COLORS,
} from "@/lib/data/air-quality";
import { AirMeasurement } from "@/lib/types/air";
import { useFormatter, useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";

type Props = {
  data: AirMeasurement[];
  component: AirQualityComponent;
  showLegend?: boolean;
  containerClassName?: string;
};

const AirComponentChart: React.FC<Props> = ({
  component,
  data,
  showLegend = true,
  containerClassName = "",
}) => {
  const t = useTranslations("air-quality");
  const bandsRaw = AIR_COMPONENT_BANDS[component] ?? [];
  const formatter = useFormatter();

  const [yMin, yMax] = useMemo(() => {
    const MAX_Y = component === "LKI" ? 11 : 30;
    if (!data.length) return [0, MAX_Y];
    const vals = data.map((d) => d.value);
    const dMin = Math.min(...vals);
    const dMax = Math.max(...vals);
    const pad = Math.max(1, (dMax - dMin) * 0.05);
    const lower = Math.ceil(Math.max(0, dMin - pad));
    const upper = Math.ceil(Math.max(MAX_Y, dMax + pad));
    return [lower, upper];
  }, [data, component]);

  return (
    <div
      className={twMerge(
        "w-full justify-around flex flex-col gap-6",
        containerClassName
      )}
    >
      {showLegend && bandsRaw.length > 0 && (
        <div className="flex flex-row gap-2 flex-wrap justify-around">
          {bandsRaw.map((b) => (
            <div key={b.q} className="flex flex-row items-center gap-1">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: AIR_QUALITY_COLORS[b.q].bg }}
              ></div>
              <span className="text-sm">{t(`bands.${b.q}`)}</span>
            </div>
          ))}
        </div>
      )}
      <ResponsiveContainer>
        <LineChart data={data} margin={{ left: 10, right: 10 }}>
          {bandsRaw.map((b, i) =>
            b.max !== null ? (
              <ReferenceLine
                key={i}
                y={b.max}
                stroke={AIR_QUALITY_COLORS[b.q].bg}
                strokeWidth={2}
                strokeDasharray="4 4"
              />
            ) : null
          )}

          <XAxis dataKey="date" tick={false} />

          <YAxis
            domain={[yMin, yMax]}
            width={48}
            tick={{ fontSize: 12 }}
            ticks={Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i)}
          />

          <Tooltip
            formatter={(v: any) => [v.toFixed(3), t("chart.value")]}
            labelFormatter={(label: any) => {
              const d = new Date(label);
              return formatter.dateTime(d, {
                dateStyle: "medium",
                timeStyle: "short",
              });
            }}
            contentStyle={{ fontSize: 12 }}
          />

          <Line
            type="monotone"
            dataKey="value"
            stroke="#0f172a"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AirComponentChart;
